// =============================================================================
// LEGAL MARKDOWN — a small, dependency-free markdown-subset parser for
// rendering docs/legal/*.md in-app. ENROLL lane (custom login + first-run
// enrollment consent): the mission's own instruction is "one source,
// rendered — don't fork content into JSX strings." Rather than add a
// markdown-library dependency (react-markdown/remark/marked — none exist in
// this app's package.json today) for a narrow, fully-controlled input (our
// own three legal docs, never third-party/untrusted markdown), this module
// parses the EXACT subset those three files actually use: h1/h2/h3 headings,
// paragraphs, bullet lists, a GFM pipe table (privacy-policy.md §4's
// provider table is the one place this shows up), inline bold/link/code, and
// HTML comments (stripped — the citation/grounding comments are for
// engineers reading the source file, never meant to render in-app).
//
// PURE: parseLegalMarkdown(markdown: string) -> LegalBlock[]. No DOM, no
// React import here — LegalDocument.tsx (the thin render layer) turns this
// AST into JSX. Kept framework-free so the parser itself is directly
// unit-testable, the same "the decision is a pure module" house pattern
// onboardingStep.ts/driveExportState.ts already establish.
//
// NOT a general-purpose markdown engine: unsupported constructs (nested
// lists, blockquotes, code fences, ordered-list restart numbers) are simply
// not needed by the three source docs today and are not handled — if a
// future legal doc needs one, extend this parser rather than reaching for a
// different rendering path (single source of truth for how legal markdown
// renders).
//
// FIX 3 (FE-FIXES lane, 2026-07-05 — flagged for team review): two real
// parser bugs, confirmed against the actual docs/legal/*.md source (not
// hypothetical inputs):
//   1. `### 2.1 Account and sign-in` (privacy-policy.md §2 has TEN of these
//      h3 subsections) matched neither `# ` nor `## ` — it fell all the way
//      to the paragraph branch, where it was rendered as a LITERAL "###
//      2.1 ..." line (and, worse, did not stop the paragraph-gathering loop
//      either, since that loop's own continuation guard only excluded `# `/
//      `## `, letting an h3 line glue into the surrounding prose). Fixed by
//      adding a real `h3` block type + recognizing `### `, checked before the
//      shorter `## `/`# ` prefixes so a future `####` extension has an
//      obvious place to slot in above these three.
//   2. A wrapped bullet's continuation line ("- Copy, modify, or create
//      derivative works of the Software itself (as distinct from" / "  your
//      own content, which you own — see §5).", eula.md §2) broke the list
//      block immediately — the old bullet loop only matched a line starting
//      with `- ` itself, so the very next (indented, non-`- `-prefixed)
//      continuation line ended the list and became its OWN separate
//      paragraph block, visually reading as the bullet ending mid-sentence.
//      CONFIRMED indentation convention, verified across all three real
//      docs (eula.md §2/§4, privacy-policy.md §2/§3/§7, terms-of-service.md
//      §2/§4/§5 — every wrapped continuation line is indented exactly two
//      spaces, matching the "- " marker's own width): a line that is
//      NON-BLANK, does not start a NEW bullet, and is INDENTED (leading
//      whitespace in the raw, untrimmed line) is a continuation of the
//      CURRENT bullet item, appended with a single space (markdown's own
//      soft-wrap convention, same joining rule the paragraph branch already
//      uses) — never its own item, never its own paragraph.
// =============================================================================

export type LegalBlock =
  | { type: 'h1'; text: string }
  | { type: 'h2'; text: string }
  | { type: 'h3'; text: string }
  | { type: 'paragraph'; text: string }
  | { type: 'list'; items: string[] }
  | { type: 'table'; headers: string[]; rows: string[][] }
  | { type: 'hr' };

/** Strip every HTML comment (`<!-- ... -->`, including multi-line ones) —
 *  the inline grounding/citation comments throughout docs/legal/*.md are for
 *  engineers reading the source, never meant to render in-app. Runs BEFORE
 *  line-splitting so a comment spanning several lines is removed cleanly. */
function stripHtmlComments(markdown: string): string {
  return markdown.replace(/<!--[\s\S]*?-->/g, '');
}

/** Whether a line looks like a GFM table separator row, e.g. `|---|---|---|`
 *  or `| --- | :--- | ---: |`. */
function isTableSeparatorLine(line: string): boolean {
  const trimmed = line.trim();
  if (!trimmed.startsWith('|')) return false;
  const cells = splitTableRow(trimmed);
  return cells.length > 0 && cells.every((c) => /^:?-{3,}:?$/.test(c.trim()));
}

/** Split a `| a | b | c |` row into `['a', 'b', 'c']`, trimming the leading/
 *  trailing empty cells produced by the outer pipes. */
function splitTableRow(line: string): string[] {
  const trimmed = line.trim();
  const withoutOuterPipes = trimmed.replace(/^\|/, '').replace(/\|$/, '');
  return withoutOuterPipes.split('|').map((c) => c.trim());
}

/** Whether a RAW (untrimmed) line is indented — starts with a space or tab.
 *  This is the confirmed continuation-line signal for a wrapped bullet item
 *  (see this module's own FIX 3 header note): every real wrapped bullet
 *  continuation across all three docs/legal/*.md files is indented exactly
 *  two spaces, matching the "- " marker's own width. Checked on the RAW
 *  line (before trimming) — a trimmed line has no leading whitespace left to
 *  test. */
function isIndentedContinuation(rawLine: string): boolean {
  return /^[ \t]/.test(rawLine) && rawLine.trim() !== '';
}

/** Whether a (trimmed) line starts a NEW top-level block this parser
 *  recognizes on its own — used by the bullet-continuation scan below to
 *  decide when a run of items must stop even if the next line happens to be
 *  indented (e.g. an indented line that is itself a new heading is never
 *  swallowed as a continuation). */
function startsOwnBlock(trimmed: string): boolean {
  return (
    trimmed === '' ||
    trimmed === '---' ||
    trimmed.startsWith('# ') ||
    trimmed.startsWith('## ') ||
    trimmed.startsWith('### ') ||
    trimmed.startsWith('- ') ||
    trimmed.startsWith('|')
  );
}

/**
 * Parse the exact markdown subset docs/legal/*.md uses into a flat block
 * list. Blank lines separate blocks; a run of `- ` lines (plus each item's
 * own indented wrapped-continuation lines, joined back in — FIX 3) becomes
 * one `list` block; a header row + separator row + body rows becomes one
 * `table` block; `---` alone on a line becomes an `hr` (the doc's own
 * section-divider convention, distinct from a table separator, which is only
 * ever recognized directly under a table header row).
 */
export function parseLegalMarkdown(markdown: string): LegalBlock[] {
  const lines = stripHtmlComments(markdown).split('\n');
  const blocks: LegalBlock[] = [];

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (trimmed === '') {
      i += 1;
      continue;
    }

    if (trimmed === '---') {
      blocks.push({ type: 'hr' });
      i += 1;
      continue;
    }

    // h3 checked before h2/h1 — a longer `#`-prefix must win over a shorter
    // one it would otherwise also satisfy if checked in the wrong order (not
    // actually ambiguous today since '###'.startsWith('## ') is false, but
    // ordering longest-prefix-first is the safe general rule if a future h4
    // is ever added above these three).
    if (trimmed.startsWith('### ')) {
      blocks.push({ type: 'h3', text: trimmed.slice(4).trim() });
      i += 1;
      continue;
    }

    if (trimmed.startsWith('## ')) {
      blocks.push({ type: 'h2', text: trimmed.slice(3).trim() });
      i += 1;
      continue;
    }

    if (trimmed.startsWith('# ')) {
      blocks.push({ type: 'h1', text: trimmed.slice(2).trim() });
      i += 1;
      continue;
    }

    // A table: this line is a header row AND the next non-consumed line is
    // a valid separator row.
    if (trimmed.startsWith('|') && i + 1 < lines.length && isTableSeparatorLine(lines[i + 1])) {
      const headers = splitTableRow(trimmed);
      i += 2; // header + separator
      const rows: string[][] = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        rows.push(splitTableRow(lines[i]));
        i += 1;
      }
      blocks.push({ type: 'table', headers, rows });
      continue;
    }

    if (trimmed.startsWith('- ')) {
      const items: string[] = [];
      while (i < lines.length) {
        const itemLine = lines[i].trim();
        if (itemLine.startsWith('- ')) {
          items.push(itemLine.slice(2).trim());
          i += 1;
          continue;
        }
        // FIX 3 — a wrapped continuation line: not blank, not a new bullet,
        // not some OTHER block type starting mid-list, but indented relative
        // to the "- " marker. Appended to the item just pushed (markdown's
        // own soft-wrap join, space-separated) rather than breaking the list
        // here and stranding it as its own paragraph.
        if (!startsOwnBlock(itemLine) && isIndentedContinuation(lines[i]) && items.length > 0) {
          items[items.length - 1] = `${items[items.length - 1]} ${itemLine}`.trim();
          i += 1;
          continue;
        }
        break;
      }
      blocks.push({ type: 'list', items });
      continue;
    }

    // A paragraph: gather consecutive non-blank, non-special lines and join
    // with a space (markdown's own soft-wrap convention — a single logical
    // paragraph is often hand-wrapped across several source lines).
    const paraLines: string[] = [];
    while (i < lines.length && !startsOwnBlock(lines[i].trim())) {
      paraLines.push(lines[i].trim());
      i += 1;
    }
    blocks.push({ type: 'paragraph', text: paraLines.join(' ') });
  }

  return blocks;
}

// =============================================================================
// INLINE SPANS — bold (`**x**`), links (`[text](url)`), and inline code
// (`` `x` ``) within a paragraph/list-item/table-cell's text. Returned as a
// flat span list (never nested — the three source docs never nest inline
// formatting) for LegalDocument.tsx to map into JSX without its own regex
// parsing.
// =============================================================================

export type InlineSpan =
  | { type: 'text'; text: string }
  | { type: 'bold'; text: string }
  | { type: 'code'; text: string }
  | { type: 'link'; text: string; href: string };

const INLINE_PATTERN = /\*\*([^*]+)\*\*|`([^`]+)`|\[([^\]]+)\]\(([^)]+)\)/g;

/** Parse inline spans out of a single line of text. Plain text between
 *  matches becomes its own `text` span so span order round-trips into the
 *  original reading order exactly. */
export function parseInlineSpans(text: string): InlineSpan[] {
  const spans: InlineSpan[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null = INLINE_PATTERN.exec(text);
  // Reset lastIndex for a fresh parse each call — INLINE_PATTERN is a module-
  // level regex with the `g` flag, whose own lastIndex persists across calls
  // unless explicitly reset here.
  INLINE_PATTERN.lastIndex = 0;
  match = INLINE_PATTERN.exec(text);

  while (match !== null) {
    if (match.index > lastIndex) {
      spans.push({ type: 'text', text: text.slice(lastIndex, match.index) });
    }
    if (match[1] !== undefined) {
      spans.push({ type: 'bold', text: match[1] });
    } else if (match[2] !== undefined) {
      spans.push({ type: 'code', text: match[2] });
    } else if (match[3] !== undefined && match[4] !== undefined) {
      spans.push({ type: 'link', text: match[3], href: match[4] });
    }
    lastIndex = INLINE_PATTERN.lastIndex;
    match = INLINE_PATTERN.exec(text);
  }
  if (lastIndex < text.length) {
    spans.push({ type: 'text', text: text.slice(lastIndex) });
  }
  return spans;
}
